import { ticketFieldsToEnvironment } from '@overlayqa/shared';
import type { MentionableUser, Ticket } from '@overlayqa/shared';

type IssueEnvironment = ReturnType<typeof ticketFieldsToEnvironment>;

export interface IssueEnvironmentResult {
  environment: IssueEnvironment;
  hasEnvironmentData: boolean;
  teamMembers: MentionableUser[];
  createdByName?: string;
}

/**
 * Pure derivation of the environment block + @mention team members for the
 * issue detail page. No hooks, no side effects: the caller owns useProject /
 * useTeam (the source of `teamData`) and any logging. Extracted verbatim from
 * IssueDetailPage so the page stays composition-only.
 */
export function buildIssueEnvironment(ticket: Ticket, teamData: unknown): IssueEnvironmentResult {
  const members =
    (teamData as { members?: Array<{ user?: { id?: string; name?: string; email?: string; avatarUrl?: string } }> } | undefined)
      ?.members ?? [];
  const teamMembers: MentionableUser[] = members
    .map((m) => m.user)
    .filter((u): u is { id: string; name: string; email?: string; avatarUrl?: string } => !!u?.id && !!(u?.name || u?.email))
    .map((u) => ({ id: u.id, name: u.name || u.email!, avatarUrl: u.avatarUrl }));

  const ticketWithEnvFields = ticket as Ticket & {
    viewportWidth?: number;
    viewportHeight?: number;
    screenWidth?: number;
    screenHeight?: number;
    devicePixelRatio?: number;
    browserName?: string;
    browserVersion?: string;
    os?: string;
  };

  const environment = ticketFieldsToEnvironment({
    browserName: ticketWithEnvFields.browserName,
    browserVersion: ticketWithEnvFields.browserVersion,
    os: ticketWithEnvFields.os,
    viewportWidth: ticketWithEnvFields.viewportWidth ?? ticket.viewport?.width,
    viewportHeight: ticketWithEnvFields.viewportHeight ?? ticket.viewport?.height,
    screenWidth: ticketWithEnvFields.screenWidth ?? ticket.screen?.width,
    screenHeight: ticketWithEnvFields.screenHeight ?? ticket.screen?.height,
    devicePixelRatio: ticketWithEnvFields.devicePixelRatio ?? ticket.viewport?.devicePixelRatio,
  });

  const hasEnvironmentData =
    environment.browser.name !== 'Unknown' ||
    environment.os !== 'Unknown' ||
    (environment.viewport.width > 0 && environment.viewport.height > 0) ||
    (environment.screen.width > 0 && environment.screen.height > 0);

  const createdByName = teamMembers.find((m) => m.id === ticket.createdById)?.name;

  return { environment, hasEnvironmentData, teamMembers, createdByName };
}
